import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import InputGroups from "@/UI/components/InputGroups";
import FilesInput from "@/UI/inputs/FilesInput";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import ProductSizePopup, { type ProductSizePopupDetails, type SizeState } from "@/UI/popups/ProductSizePopup";
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LangValue {
    lang: string;
    value: string;
    default?: boolean;
}

interface InitialProductData {
    _id?: string;
    name: LangValue[];
    description: LangValue[];
    pictures: string[];
    temperature: "hot" | "cold" | null;
    discount: { type: "percentage" | "fixed"; amount: number } | null;
    sizes: Array<{
        title: LangValue[];
        showTitle: boolean;
        price: number;
        discount: { type: "percentage" | "fixed"; amount: number } | null;
    }>;
    category: Array<{ _id: string; name: string } | string>;
    sort: number;
}

export interface ProductsFormDetails {
    productId?: string;
    initialData?: InitialProductData;
    isDuplicate?: boolean;
    onSuccess: () => void;
}

interface FormState {
    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    temperature: "hot" | "cold" | "";
    hasDiscount: boolean;
    discountType: "percentage" | "fixed";
    discountAmount: string;
    sizes: SizeState[];
    category: string[];
    sort: string;
    existingPictures: string[];
    newPictures: File[];
}

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getLangValue(arr: LangValue[] | undefined, lang: string): string {
    if (!arr || !Array.isArray(arr)) return "";
    return arr.find((item) => item.lang === lang)?.value ?? "";
}

function buildInitialForm(initial: InitialProductData | undefined, isDuplicate?: boolean): FormState {
    if (!initial) {
        return {
            nameAr: "",
            nameEn: "",
            descriptionAr: "",
            descriptionEn: "",
            temperature: "",
            hasDiscount: false,
            discountType: "percentage",
            discountAmount: "",
            sizes: [],
            category: [],
            sort: "0",
            existingPictures: [],
            newPictures: [],
        };
    }

    // Extract category IDs from the array
    const categoryIds = (initial.category || []).map((cat) => (typeof cat === "string" ? cat : cat._id));

    return {
        nameAr: getLangValue(initial.name, "ar"),
        nameEn: getLangValue(initial.name, "en"),
        descriptionAr: getLangValue(initial.description, "ar"),
        descriptionEn: getLangValue(initial.description, "en"),
        temperature: initial.temperature ?? "",
        hasDiscount: !!initial.discount,
        discountType: initial.discount?.type ?? "percentage",
        discountAmount: initial.discount?.amount?.toString() ?? "",
        sizes: initial.sizes.map((s) => ({
            titleAr: getLangValue(s.title, "ar"),
            titleEn: getLangValue(s.title, "en"),
            showTitle: s.showTitle,
            price: s.price?.toString() ?? "",
            hasDiscount: !!s.discount,
            discountType: s.discount?.type ?? "percentage",
            discountAmount: s.discount?.amount?.toString() ?? "",
        })),
        category: categoryIds,
        sort: initial.sort?.toString() ?? "0",
        existingPictures: isDuplicate ? [] : (initial.pictures ?? []),
        newPictures: [],
    };
}

function buildFormData(form: FormState): FormData {
    const fd = new FormData();

    const name = [
        ...(form.nameAr.trim() ? [{ lang: "ar", value: form.nameAr.trim(), default: true }] : []),
        ...(form.nameEn.trim() ? [{ lang: "en", value: form.nameEn.trim(), default: false }] : []),
    ];

    const description = [
        ...(form.descriptionAr.trim() ? [{ lang: "ar", value: form.descriptionAr.trim(), default: true }] : []),
        ...(form.descriptionEn.trim() ? [{ lang: "en", value: form.descriptionEn.trim(), default: false }] : []),
    ];

    const sizes = form.sizes.map((s) => ({
        title: [
            ...(s.titleAr.trim() ? [{ lang: "ar", value: s.titleAr.trim(), default: true }] : []),
            ...(s.titleEn.trim() ? [{ lang: "en", value: s.titleEn.trim(), default: false }] : []),
        ],
        showTitle: s.showTitle,
        price: parseFloat(s.price) || 0,
        discount: s.hasDiscount ? { type: s.discountType, amount: parseFloat(s.discountAmount) || 0 } : null,
    }));

    fd.append("name", JSON.stringify(name));
    fd.append("description", JSON.stringify(description));
    fd.append("temperature", form.temperature || "");
    fd.append("category", JSON.stringify(form.category || []));
    fd.append("sort", form.sort || "0");
    fd.append("sizes", JSON.stringify(sizes));

    if (form.hasDiscount) {
        fd.append("discount", JSON.stringify({ type: form.discountType, amount: parseFloat(form.discountAmount) || 0 }));
    }

    // Existing pictures (kept URLs) as JSON array
    fd.append("pictures", JSON.stringify(form.existingPictures));

    // New pictures (files)
    form.newPictures.forEach((file) => {
        fd.append("newPictures", file);
    });

    return fd;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    details?: ProductsFormDetails;
}

export default function ProductsForm({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();

    const productId = details?.productId;
    const isDuplicate = details?.isDuplicate;
    const isEdit = !!productId && !isDuplicate;

    const [form, setForm] = useState<FormState>(() => buildInitialForm(details?.initialData, isDuplicate));

    const picturesInputRef = useRef<HTMLInputElement>(null);

    // ── Categories ────────────────────────────────────────────────────────────
    const { data: catData, loading: catLoading } = useFetch("products/categories");
    const categories: { _id: string; name: string }[] = catData?.data?.categories ?? [];

    // ── Helpers ───────────────────────────────────────────────────────────────

    const showError = (message: string) => {
        const name = "product-form-error";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                variant: "danger",
                confirmLabel: t.productForm.ok,
                onConfirm: () => {},
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // ── Size Popup ────────────────────────────────────────────────────────────

    const openSizePopup = (index?: number) => {
        const name = "product-size-popup";
        removeComp(name);

        const initialSize = index !== undefined ? form.sizes[index] : undefined;

        addComp({
            name,
            Component: ProductSizePopup,
            details: {
                initialSize,
                onSave: (size: SizeState) => {
                    if (index !== undefined) {
                        // Edit existing
                        setForm((prev) => {
                            const sizes = [...prev.sizes];
                            sizes[index] = size;
                            return { ...prev, sizes };
                        });
                    } else {
                        // Add new
                        setForm((prev) => ({ ...prev, sizes: [...prev.sizes, size] }));
                    }
                },
            } as ProductSizePopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const removeSize = (index: number) => {
        setForm((prev) => ({ ...prev, sizes: prev.sizes.filter((_, i) => i !== index) }));
    };

    const confirmRemoveSize = (index: number) => {
        const name = "confirm-delete-size";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.productForm.deleteSizeConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => removeSize(index),
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Pictures ──────────────────────────────────────────────────────────────

    const handleAddPicturesClick = () => {
        picturesInputRef.current?.click();
    };

    const handleNewPicturesChange = (files: File[]) => {
        setField("newPictures", files);
    };

    const handleRemoveExistingPicture = (url: string) => {
        setForm((prev) => ({
            ...prev,
            existingPictures: prev.existingPictures.filter((u) => u !== url),
        }));
    };

    // ── Submit fetch ──────────────────────────────────────────────────────────

    const { loading: submitting, error: submitError, status: submitStatus, invoke: submitInvoke } = useFetch("products", {}, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 201 || submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.nameAr.trim() && !form.nameEn.trim()) {
            showError(t.productForm.nameRequired);
            return;
        }

        const formData = buildFormData(form);

        submitInvoke({
            method: isEdit ? "PUT" : "POST",
            ...(isEdit ? { param: productId } : {}),
            formData,
        });
    };

    // ── Derived ────────────────────────────────────────────────────────────────

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");
    const textFieldSxRtl = getTextFieldSx("rtl");
    const textFieldSxLtr = getTextFieldSx("ltr");

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <form className="products-form" onSubmit={handleSubmit} noValidate>
            <div className="products-form-fields">
                {/* ── Name Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.productForm.name}>
                    <div className="pf-row pf-row--2">
                        <TextField
                            fullWidth
                            label={t.productForm.arabic}
                            value={form.nameAr}
                            onChange={(e) => setField("nameAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            label={t.productForm.english}
                            value={form.nameEn}
                            onChange={(e) => setField("nameEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Description Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.productForm.description}>
                    <div className="pf-row pf-row--2">
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t.productForm.arabic}
                            value={form.descriptionAr}
                            onChange={(e) => setField("descriptionAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t.productForm.english}
                            value={form.descriptionEn}
                            onChange={(e) => setField("descriptionEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Category & Sort & Temperature Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.productForm.categorization}>
                    <FormControl fullWidth disabled={catLoading} sx={textFieldSx}>
                        <InputLabel id="category-label">{t.productForm.category}</InputLabel>
                        <Select
                            labelId="category-label"
                            multiple
                            value={form.category}
                            onChange={(e) => setField("category", e.target.value as string[])}
                            input={<OutlinedInput label={t.productForm.category} />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((id) => {
                                        const category = categories.find((c) => c._id === id);
                                        return category ? (
                                            <Chip
                                                key={id}
                                                label={category.name}
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#fff4ed",
                                                    color: "#e97730",
                                                    border: "1px solid #ffd4b8",
                                                    height: "24px",
                                                    "& .MuiChip-label": {
                                                        fontSize: "12px",
                                                        fontWeight: 600,
                                                        padding: "0 8px",
                                                    },
                                                }}
                                            />
                                        ) : null;
                                    })}
                                </Box>
                            )}
                            MenuProps={{
                                slotProps: {
                                    paper: {
                                        style: {
                                            maxHeight: 48 * 4.5 + 8,
                                        },
                                    },
                                },
                            }}
                        >
                            {categories.map((c) => (
                                <MenuItem key={c._id} value={c._id}>
                                    <Checkbox
                                        checked={form.category.indexOf(c._id) > -1}
                                        sx={{
                                            color: "var(--color-gray-02)",
                                            "&.Mui-checked": {
                                                color: "var(--color-orange)",
                                            },
                                        }}
                                    />
                                    <ListItemText primary={c.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <div className="pf-row pf-row--3">
                        <TextField
                            fullWidth
                            type="number"
                            label={t.productForm.sort}
                            value={form.sort}
                            onChange={(e) => setField("sort", e.target.value)}
                            slotProps={{ htmlInput: { min: 0 } }}
                            sx={textFieldSx}
                        />

                        <TextField
                            select
                            fullWidth
                            label={t.productForm.temperature}
                            value={form.temperature}
                            onChange={(e) => setField("temperature", e.target.value as FormState["temperature"])}
                            sx={textFieldSx}
                        >
                            <MenuItem value="">{t.productForm.tempNone}</MenuItem>
                            <MenuItem value="hot">{t.productForm.tempHot}</MenuItem>
                            <MenuItem value="cold">{t.productForm.tempCold}</MenuItem>
                        </TextField>
                    </div>
                </InputGroups>

                {/* ── Discount Section ── */}
                <InputGroups
                    icon="/icons/inputs-group-icon.svg"
                    title={t.productForm.discountSection}
                    toggle={{
                        checked: form.hasDiscount,
                        onChange: (checked) => setField("hasDiscount", checked),
                    }}
                >
                    {form.hasDiscount && (
                        <div className="pf-row pf-row--2">
                            <TextField
                                select
                                fullWidth
                                label={t.productForm.discountType}
                                value={form.discountType}
                                onChange={(e) => setField("discountType", e.target.value as "percentage" | "fixed")}
                                sx={textFieldSx}
                            >
                                <MenuItem value="percentage">{t.productForm.discountPercentage}</MenuItem>
                                <MenuItem value="fixed">{t.productForm.discountFixed}</MenuItem>
                            </TextField>
                            <TextField
                                fullWidth
                                type="number"
                                label={t.productForm.discountAmount}
                                value={form.discountAmount}
                                onChange={(e) => setField("discountAmount", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                sx={textFieldSx}
                            />
                        </div>
                    )}
                </InputGroups>

                {/* ── Sizes Section ── */}
                <InputGroups
                    icon="/icons/inputs-group-icon.svg"
                    title={t.productForm.sizesSection}
                    addButton={{
                        label: t.productForm.addSize,
                        onClick: () => openSizePopup(),
                    }}
                >
                    {form.sizes.length > 0 ? (
                        <div className="pf-sizes-list">
                            {form.sizes.map((size, i) => (
                                <div key={i} className="pf-size-card">
                                    {size.showTitle && (size.titleAr || size.titleEn) && (
                                        <div className="pf-size-card-field">
                                            <div className="pf-size-card-icon">
                                                <img src="/icons/size-squared-icon.svg" alt="" width={15} height={15} />
                                            </div>
                                            <span className="pf-size-card-value">{size.titleAr || size.titleEn}</span>
                                        </div>
                                    )}
                                    <div className="pf-size-card-field">
                                        <div className="pf-size-card-icon">
                                            <img src="/icons/price-icon.svg" alt="" width={15} height={15} />
                                        </div>
                                        <span className="pf-size-card-value">{size.price || "0"}</span>
                                    </div>
                                    <button type="button" className="pf-size-card-btn pf-size-card-btn--edit" onClick={() => openSizePopup(i)}>
                                        <img src="/icons/edit-icon.svg" alt="" width={14} height={14} />
                                    </button>
                                    <button type="button" className="pf-size-card-btn pf-size-card-btn--delete" onClick={() => confirmRemoveSize(i)}>
                                        <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="pf-empty">{t.table.empty}</div>
                    )}
                </InputGroups>

                {/* ── Pictures Section ── */}
                <InputGroups
                    icon="/icons/inputs-group-icon.svg"
                    title={t.productForm.picturesSection}
                    addButton={{
                        label: t.productForm.addPicture,
                        onClick: handleAddPicturesClick,
                    }}
                >
                    {form.existingPictures.length > 0 || form.newPictures.length > 0 ? (
                        <FilesInput
                            files={form.newPictures}
                            existingUrls={form.existingPictures}
                            onChange={handleNewPicturesChange}
                            onRemoveExisting={handleRemoveExistingPicture}
                            inputRef={picturesInputRef}
                        />
                    ) : (
                        <>
                            <div className="pf-empty">{t.table.empty}</div>
                            <FilesInput
                                files={form.newPictures}
                                existingUrls={form.existingPictures}
                                onChange={handleNewPicturesChange}
                                onRemoveExisting={handleRemoveExistingPicture}
                                inputRef={picturesInputRef}
                            />
                        </>
                    )}
                </InputGroups>
            </div>

            {/* ── Footer ── */}
            <div className="pf-footer">
                <button type="button" className="pf-btn pf-btn--cancel" onClick={onClose} disabled={submitting}>
                    {t.productForm.cancel}
                </button>
                <button type="submit" className="pf-btn pf-btn--submit" disabled={submitting}>
                    {submitting ? <span className="pf-spinner" /> : isEdit ? t.productForm.update : t.productForm.create}
                </button>
            </div>
        </form>
    );
}
